/**
 * Que una corrección cuadre, antes de asentarse.
 *
 * The owner, 2026-10-15: «please make sure everything ties out every time a correction is made».
 * Every correction of money (a till's count, a purchase's cost or how it was paid) is held to its
 * own effects before the books take any of it, the way «Cada cuenta con su detalle» holds the
 * books to their details afterwards (`reportes/cuadres.ts`):
 *
 * - its entry balances;
 * - what it moves on «Caja (gaveta)», on «Efectivo por recibir» and on «Caja dólares» is what the
 *   tills it changes now hold, wait to hand over, and carry in dollars, by their own rows;
 * - what it moves on «Sobrante» and «Faltante», a till's or general cash's, is what the counts it
 *   changes now find (`conteosCorregidos`), each account once, never past zero on either.
 *
 * A correction that does not is refused, and nothing of it is booked. The tests hold every event to
 * the same, and more, after it is booked (`pruebas/memoria.ts`); this is the books doing it
 * themselves, on the shop's own data, every time.
 */

import { ACCOUNTS, type AccountKey } from '../ledger/accounts.js';
import type { Effect, SesionCaja } from '../state.js';
import { efectivoEsperado } from './caja.js';
import { DE_LA_CAJA, DEL_GENERAL, type CuentasDelConteo } from './encontrado.js';

/** A count a correction changes, as it found before it: what the over/short lines must come from. */
export interface ConteoQueCambia {
  id: string;
  cuentas: CuentasDelConteo;
  antes: number;
}

/** What a till holds in its drawer for «Caja (gaveta)», as `cuadres` reads it. */
function enLaGaveta(s: SesionCaja, relevadas: ReadonlySet<string>): number {
  const tiene = s.estado === 'abierta' ? efectivoEsperado(s) : relevadas.has(s.id) ? 0 : (s.queda ?? 0);
  const sinCorte = s.relevoDe ? 0 : Math.max((s.esperadoApertura ?? 0) - (s.deGeneral ?? 0), 0);
  return tiene - sinCorte;
}

const porRecibir = (s: SesionCaja) => (s.porRecibir ?? 0) + (s.porRecibirValor ?? 0);
const dolares = (s: SesionCaja) => (s.estado === 'abierta' ? (s.dolaresContables ?? 0) : 0);

/**
 * Why a correction's effects do not tie out, or null when they do. `antes`: the tills it changes as
 * they were (and which of them a shift has opened on since), and the counts it changes, each with
 * what it found before.
 */
export function noCuadraLaCorreccion(
  efectos: readonly Effect[],
  antes: { sesiones: readonly SesionCaja[]; relevadas: ReadonlySet<string>; conteos: readonly ConteoQueCambia[]; corregidos: Readonly<Record<string, number>> },
): string | null {
  // Each account's lines, debit positive.
  const movido = new Map<string, number>();
  for (const e of efectos) {
    if (e.kind !== 'asiento') continue;
    let debe = 0;
    let haber = 0;
    for (const l of e.entry.lineas) {
      debe += l.debe;
      haber += l.haber;
      movido.set(l.cuenta, (movido.get(l.cuenta) ?? 0) + l.debe - l.haber);
    }
    if (debe !== haber) return `el asiento no cuadra: debe ${debe}, haber ${haber}`;
  }
  const en = (cuenta: AccountKey) => movido.get(ACCOUNTS[cuenta].code) ?? 0;

  // The tills, as they become.
  const despues = antes.sesiones.map((s) => {
    let fila = s;
    for (const e of efectos) if (e.kind === 'sesion.patch' && e.id === s.id) fila = { ...fila, ...e.patch } as SesionCaja;
    return fila;
  });
  for (const e of efectos) {
    if (e.kind === 'sesion.patch' && !antes.sesiones.some((s) => s.id === e.id)) return 'cambia una caja que no dice cómo estaba';
  }
  const suma = (f: (s: SesionCaja) => number) =>
    despues.reduce((t, s) => t + f(s), 0) - antes.sesiones.reduce((t, s) => t + f(s), 0);
  const gaveta = suma((s) => enLaGaveta(s, antes.relevadas));
  if (en('CAJA_CORDOBAS') !== gaveta) return `la gaveta: los libros mueven ${en('CAJA_CORDOBAS')}, las cajas ${gaveta}`;
  if (en('EFECTIVO_POR_RECIBIR') !== suma(porRecibir)) return `lo por recibir: los libros mueven ${en('EFECTIVO_POR_RECIBIR')}, las cajas ${suma(porRecibir)}`;
  if (en('CAJA_DOLARES') !== suma(dolares)) return `los dólares: los libros mueven ${en('CAJA_DOLARES')}, las cajas ${suma(dolares)}`;

  // What the counts find now, and so what their accounts should move: each count once.
  let nuevos: Readonly<Record<string, number>> = antes.corregidos;
  for (const e of efectos) if (e.kind === 'config.set' && e.clave === 'conteosCorregidos') nuevos = e.valor as Record<string, number>;
  const esperado = new Map<string, number>();
  const sumarA = (cuenta: AccountKey, monto: number) => esperado.set(ACCOUNTS[cuenta].code, (esperado.get(ACCOUNTS[cuenta].code) ?? 0) + monto);
  for (const c of antes.conteos) {
    const ahora = nuevos[c.id] ?? c.antes;
    // A credit on «Sobrante» for what it finds over more, a debit on «Faltante» for what it finds short more.
    sumarA(c.cuentas.sobra, -(Math.max(ahora, 0) - Math.max(c.antes, 0)));
    sumarA(c.cuentas.falta, Math.max(-ahora, 0) - Math.max(-c.antes, 0));
  }
  for (const id of Object.keys(nuevos)) {
    if (nuevos[id] !== antes.corregidos[id] && !antes.conteos.some((c) => c.id === id)) return 'cambia lo que encontró un conteo que no dice cómo estaba';
  }
  for (const cuentas of [DE_LA_CAJA, DEL_GENERAL]) {
    for (const cuenta of [cuentas.sobra, cuentas.falta]) {
      const debe = esperado.get(ACCOUNTS[cuenta].code) ?? 0;
      if (en(cuenta) !== debe) return `${ACCOUNTS[cuenta].name}: los libros mueven ${en(cuenta)}, lo que encontraron los conteos ${debe}`;
    }
  }
  return null;
}
